import { useQuery } from "@tanstack/react-query";
import qs from "qs";
import api from "../api"; // axios instance
import Cookies from "js-cookie";

export interface Ticket {
  id: number;
  title: string;
  status: string;
  [key: string]: any;
}

export interface TicketsResponse {
  tickets: Ticket[];
  meta: {
    total: number;
    page: number;
    perPage: number;
  };
}

export interface TicketFilters {
  type?: "all" | "assigned"; // made optional
  page?: number;
  perPage?: number;
  keyword?: string;
  search?: boolean | string;
  ticket_ids_in?: any[];
  ticket_ids_not_in?: any[];
  status_in?: string[];
  status_not_in?: any[];
  ticket_type_in?: string[];
  ticket_type_not_in?: string[];
  call_types_in?: string[];
  call_types_not_in?: string[];
  call_types_contain?: string[];
  call_types_not_contain?: string[];
  assigned_to_in?: number[];
  assigned_to_not_in?: number[];
  assigned_to_is_empty?: number[];
  assigned_to_is_not_empty?: number[];
  created_by_in?: number[];
  created_by_not_in?: number[];
  created_date_in?: string[];
  created_date_not_in?: string[];
  created_last_week?: boolean;
  last_updated_this_week?: boolean;
  service_ticket_no?: string;
}

export interface FetchTicketsParams {
  type: "all" | "assigned"; // ✅ required by API
  page?: number;
  perPage?: number;
  keyword?: string;
  search?: boolean | string; // ✅ explicitly add
  ticket_ids_in?: any[];
  ticket_ids_not_in?: any[];
  status_in?: string[];
  status_not_in?: any[];
  ticket_type_in?: string[];
  ticket_type_not_in?: string[];
  call_types_in?: string[];
  call_types_not_in?: string[];
  call_types_contain?: string[];
  call_types_not_contain?: string[];
  assigned_to_in?: number[];
  assigned_to_not_in?: number[];
  assigned_to_is_empty?: number[];
  assigned_to_is_not_empty?: number[];
  created_by_in?: number[];
  created_by_not_in?: number[];
  created_date_in?: string[];
  created_date_not_in?: string[];
  created_last_week?: boolean;
  last_updated_this_week?: boolean;
  service_ticket_no?: string;
}

const fetchTickets = async (filters: TicketFilters = {}, paginate = true) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized");

  const params: Record<string, any> = {
    client_type: "ADMIN",
    type: filters.type ?? "all", // ✅ required by API
    ...filters,
  };

  if (paginate) {
    params.paginate = true;
    params.page = filters.page ?? 1;
    params.per_page = filters.perPage ?? 10;
  } else {
    delete params.page;
    delete params.per_page;
    delete params.paginate;
    delete params.perPage;
  }

  try {
    const { data } = await api.get<{ data: TicketsResponse }>("/tickets/v1", {
      headers: { Authorization: `Bearer ${token}` },
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            if (key === "status" && value.length === 1) {
              searchParams.append(key, String(value[0]));
            } else {
              searchParams.append(key, `[${value.join(",")}]`);
            }
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });

        return searchParams.toString();
      },
    });

    return data.data;
  } catch (error: any) {
    // ✅ Handle 404 gracefully
    if (error.response?.status === 404) {
      return {
        tickets: [],
      };
    }

    // ❌ Other errors should still throw
    throw error;
  }
};

export const useTickets = (
  filters: TicketFilters = {},
  paginate = true,
  currentPage = 1,
  perPage = 10
) => {
  return useQuery({
    queryKey: ["tickets", { filters, paginate, currentPage, perPage }],
    queryFn: () =>
      fetchTickets({ ...filters, page: currentPage, perPage }, paginate),
    keepPreviousData: true,
    staleTime: 0, // always re-fetch when queryKey changes (e.g., page)
  });
};
